import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { SeasonState } from './season.state';
import {
  errorState,
  loadedState,
  loadingState,
  isLoadedState,
} from 'ngx-http-request-state';
import {
  cupOverviewComponentActions,
  leagueOverviewComponentActions,
  routerActions,
  seasonApiActions,
  seasonInfoComponentActions,
} from './season.actions';
import { toError } from '@pool-league/error';
import { slugToDisplayName } from '@pool-league/frontend-shared-util-season';
import { Format } from '@pool-league/shared-data-api';
import { leagueApiActions } from '@pool-league/frontend-web-data-league';

const NO_SEASON_SELECTED: SeasonState = {
  season: loadingState({ displayName: '' }),
  statusTransitionRequest: undefined,
};

export const seasonFeature = createFeature({
  name: 'season',
  reducer: createReducer<SeasonState>(
    NO_SEASON_SELECTED,
    on(routerActions.seasonClosed, () => NO_SEASON_SELECTED),
    on(routerActions.seasonOpened, (state, { seasonSlug }) => ({
      ...state,
      season: loadingState({ displayName: slugToDisplayName(seasonSlug) }),
      statusTransitionRequest: undefined,
    })),
    on(seasonApiActions.seasonLoadError, (state, { seasonSlug, error }) => ({
      ...state,
      season: errorState(toError(error), {
        displayName: slugToDisplayName(seasonSlug),
      }),
    })),
    on(seasonApiActions.seasonLoaded, (state, { season }) => ({
      ...state,
      season: loadedState(season),
      statusTransitionRequest: undefined,
    })),
    on(seasonApiActions.seasonStatusUpdated, (state, { season }) => ({
      ...state,
      season: loadedState({
        competitions: {},
        ...state.season.value,
        ...season,
      }),
      statusTransitionRequest: undefined,
    })),
    on(seasonInfoComponentActions.transitionStatus, (state) => ({
      ...state,
      statusTransitionRequest: loadingState<void>(),
    })),
    on(seasonApiActions.transitionStatusError, (state, { error }) => ({
      ...state,
      statusTransitionRequest: errorState(error),
    })),
    on(
      leagueApiActions.deleteResultSuccess,
      leagueApiActions.saveResultSuccess,
      (state, { leagueProgress }) => ({
        ...state,
        season: isLoadedState(state.season)
          ? {
              ...state.season,
              value: {
                ...state.season.value,
                competitions: {
                  ...state.season.value.competitions,
                  eightBallRoundRobin: state.season.value.competitions
                    .eightBallRoundRobin
                    ? {
                        ...state.season.value.competitions.eightBallRoundRobin,
                        progress: leagueProgress,
                      }
                    : undefined,
                },
              },
            }
          : state.season,
      }),
    ),
    on(
      leagueApiActions.leagueCreated,
      (state, { seasonId, format, details }) => {
        console.log(state.season.value.id, { seasonId, format, details });
        if (
          !isLoadedState(state.season) ||
          seasonId !== state.season.value.id
        ) {
          console.warn('Ignoring!!');
          return state;
        }
        return {
          ...state,
          season: {
            ...state.season,
            value: {
              ...state.season.value,
              competitions: {
                ...state.season.value.competitions,
                [getLeagueFormatKey(format)]: details,
              },
            },
          },
        };
      },
    ),
    on(
      cupOverviewComponentActions.entrantsUpdated,
      (state, { format, seasonId, entrants }) => {
        if (
          !isLoadedState(state.season) ||
          seasonId !== state.season.value.id
        ) {
          // should never happen
          return state;
        }
        const key = getCupFormatKey(format);
        const cup = state.season.value.competitions[key];
        if (!cup) {
          console.error(new Error(`Competition ${key} not defined!`));
          return state;
        }
        return {
          ...state,
          season: loadedState({
            ...state.season.value,
            competitions: {
              ...state.season.value.competitions,
              [key]: {
                ...cup,
                entrants,
              },
            },
          }),
        };
      },
    ),
    on(
      cupOverviewComponentActions.cupCreated,
      cupOverviewComponentActions.cupStatusUpdated,
      (state, { format, seasonId, details }) => {
        if (
          !isLoadedState(state.season) ||
          seasonId !== state.season.value.id
        ) {
          return state;
        }
        const key = getCupFormatKey(format);
        return {
          ...state,
          season: loadedState({
            ...state.season.value,
            competitions: {
              ...state.season.value.competitions,
              [key]: details,
            },
          }),
        };
      },
    ),
    on(
      leagueOverviewComponentActions.leagueStatusUpdated,
      (state, { format, seasonId, details }) => {
        if (
          !isLoadedState(state.season) ||
          seasonId !== state.season.value.id
        ) {
          return state;
        }
        const key = getLeagueFormatKey(format);
        return {
          ...state,
          season: loadedState({
            ...state.season.value,
            competitions: {
              ...state.season.value.competitions,
              [key]: details,
            },
          }),
        };
      },
    ),
  ),
  extraSelectors: ({ selectSeason }) => ({
    selectSeasonId: createSelector(selectSeason, (seasonRequestState) =>
      isLoadedState(seasonRequestState)
        ? seasonRequestState.value.id
        : undefined,
    ),
  }),
});

function getCupFormatKey(format: Format) {
  if (format !== Format.EightBall) {
    throw new Error('Only 8-ball format competitions currently supported');
  }
  return 'eightBallKnockout' as const;
}

function getLeagueFormatKey(format: Format) {
  if (format !== Format.EightBall) {
    throw new Error('Only 8-ball format competitions currently supported');
  }
  return 'eightBallRoundRobin' as const;
}
